/**
 * Toolbar that appears under the mode bar when the user is in 'measure' mode.
 * Surfaces sub-tool selection (linear / polyline / area / angle / select),
 * calibration status, and quick actions.
 */

import React from 'react';
import {
  Ruler,
  Spline,
  Square,
  Triangle,
  MousePointer2,
  AlertTriangle,
  CheckCircle2,
  Settings2,
  Trash2,
} from 'lucide-react';
import { getStandardScaleById } from '../../utils/measurementMath';
import type { PdfMeasurementCalibration } from '../../types';
import type { MeasurementSubMode } from './MeasurementLayer';
import './MeasurementToolbar.css';

export interface MeasurementToolbarProps {
  subMode: MeasurementSubMode;
  onSubModeChange: (mode: MeasurementSubMode) => void;
  calibration: PdfMeasurementCalibration | null;
  pageNumber: number;
  onOpenCalibration: () => void;
  onClearCalibration: () => void;
  onClearMeasurements: () => void;
  /** Optional explicit display unit override; null means "use the calibration default". */
  displayUnitOverride: 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm' | null;
  onDisplayUnitOverrideChange: (next: 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm' | null) => void;
  /** True while the user is holding the pitch-mode key (P). */
  pitchMode?: boolean;
}

function describeCalibration(cal: PdfMeasurementCalibration | null): string {
  if (!cal) return 'No scale set on this page';
  if (cal.mode === 'standard' && cal.standardScaleId) {
    const s = getStandardScaleById(cal.standardScaleId);
    if (s) return s.label;
  }
  if (cal.mode === 'two-point' && cal.realDistance != null) {
    return `Custom (${cal.realDistance} ${cal.realDistanceUnit || ''})`;
  }
  return 'Custom scale';
}

export function MeasurementToolbar({
  subMode,
  onSubModeChange,
  calibration,
  pageNumber,
  onOpenCalibration,
  onClearCalibration,
  onClearMeasurements,
  displayUnitOverride,
  onDisplayUnitOverrideChange,
  pitchMode = false,
}: MeasurementToolbarProps) {
  const calibrated = !!calibration;
  const isMetric = calibration?.unitSystem === 'metric';
  const unitOptions: { value: 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm' | 'auto'; label: string }[] = isMetric
    ? [
        { value: 'auto', label: 'auto' },
        { value: 'mm', label: 'mm' },
        { value: 'cm', label: 'cm' },
        { value: 'm', label: 'm' },
      ]
    : [
        { value: 'auto', label: 'auto' },
        { value: 'ft-in', label: "ft' in\"" },
        { value: 'ft', label: 'ft' },
        { value: 'in', label: 'in' },
      ];

  return (
    <div className="mt-toolbar" role="toolbar" aria-label="Measurement tools">
      <div className="mt-tool-group">
        <button
          type="button"
          className={`mt-tool-btn ${subMode === 'linear' ? 'active' : ''}`}
          onClick={() => onSubModeChange('linear')}
          title="Linear distance (2 clicks). Hold P while measuring to also show roof pitch (X/12)."
        >
          <Ruler size={14} />
          <span>Linear</span>
        </button>
        <button
          type="button"
          className={`mt-tool-btn ${subMode === 'polyline' ? 'active' : ''}`}
          onClick={() => onSubModeChange('polyline')}
          title="Polyline (multi-click, double-click to finish)"
        >
          <Spline size={14} />
          <span>Polyline</span>
        </button>
        <button
          type="button"
          className={`mt-tool-btn ${subMode === 'area' ? 'active' : ''}`}
          onClick={() => onSubModeChange('area')}
          title="Area (close polygon)"
        >
          <Square size={14} />
          <span>Area</span>
        </button>
        <button
          type="button"
          className={`mt-tool-btn ${subMode === 'angle' ? 'active' : ''}`}
          onClick={() => onSubModeChange('angle')}
          title="Angle (3 clicks: ray, vertex, ray). Hold P to display as roof pitch (X/12) instead of degrees."
        >
          <Triangle size={14} />
          <span>Angle</span>
        </button>
        <button
          type="button"
          className={`mt-tool-btn ${subMode === 'select' ? 'active' : ''}`}
          onClick={() => onSubModeChange('select')}
          title="Select / move / delete (click a measurement, drag a vertex, press Delete)"
        >
          <MousePointer2 size={14} />
          <span>Select</span>
        </button>
      </div>

      <span className={`mt-status ${calibrated ? 'mt-status--calibrated' : 'mt-status--uncalibrated'}`}>
        {calibrated ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
        <span>{describeCalibration(calibration)} · page {pageNumber}</span>
      </span>

      {(subMode === 'angle' || subMode === 'linear' || pitchMode) && (
        <span className={`mt-pitch-hint ${pitchMode ? 'mt-pitch-hint--active' : ''}`}>
          {pitchMode ? (
            <>Pitch mode · release <kbd>P</kbd> for {subMode === 'angle' ? 'degrees' : 'length only'}</>
          ) : (
            <>Hold <kbd>P</kbd> for roof pitch</>
          )}
        </span>
      )}

      <div className="mt-spacer" />

      {calibrated && (
        <select
          className="mt-unit-select"
          value={displayUnitOverride || 'auto'}
          onChange={e => {
            const v = e.target.value as 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm' | 'auto';
            onDisplayUnitOverrideChange(v === 'auto' ? null : v);
          }}
          title="Label units"
        >
          {unitOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      <button
        type="button"
        className={calibrated ? 'mt-action-btn' : 'mt-action-btn mt-action-btn--primary'}
        onClick={onOpenCalibration}
      >
        <Settings2 size={13} />
        <span>{calibrated ? 'Change scale' : 'Set scale'}</span>
      </button>

      {calibrated && (
        <button
          type="button"
          className="mt-action-btn mt-action-btn--danger"
          onClick={onClearCalibration}
          title="Remove this page's calibration"
        >
          Clear scale
        </button>
      )}

      <button
        type="button"
        className="mt-action-btn mt-action-btn--danger"
        onClick={onClearMeasurements}
        title="Delete every measurement on this page"
      >
        <Trash2 size={13} />
        <span>Clear page</span>
      </button>
    </div>
  );
}
